import React from 'react';
import DashboardCard from '../shared/DashboardCard';
import EmptyState from '../shared/EmptyState';
import styles from '../../../../styles/DashboardV2.module.css';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    ReferenceLine,
} from 'recharts';

interface AgentStat {
    name: string;
    completionRate: number;
    completedItems: number;
    totalItems: number;
}

interface TeamComplianceChartWidgetProps {
    agents: AgentStat[];
    loading?: boolean;
}

const getBarColor = (rate: number) => {
    if (rate >= 90) return '#10b981';
    if (rate >= 70) return '#f59e0b';
    return '#ef4444';
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
        const d = payload[0].payload as AgentStat;
        return (
            <div className={styles.chartTooltip}>
                <strong>{label}</strong>
                <div>{d.completionRate.toFixed(1)}% ({d.completedItems}/{d.totalItems})</div>
            </div>
        );
    }
    return null;
};

const TeamComplianceChartWidget: React.FC<TeamComplianceChartWidgetProps> = ({
    agents,
    loading,
}) => {
    const sorted = [...(agents ?? [])].sort((a, b) => b.completionRate - a.completionRate);

    return (
        <DashboardCard
            title="Team Compliance"
            loading={loading}
        >
            {sorted.length === 0 ? (
                <EmptyState icon="👥" message="No agent data available." />
            ) : (
                <ResponsiveContainer width="100%" height={Math.max(180, sorted.length * 40)}>
                    <BarChart
                        data={sorted}
                        layout="vertical"
                        margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                    >
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tickFormatter={v => `${v}%`}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            dataKey="name"
                            type="category"
                            width={110}
                            tick={{ fontSize: 12, fill: '#374151' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
                        <ReferenceLine x={80} stroke="#e2e8f0" strokeDasharray="4 4" label={{ value: '80%', position: 'top', fontSize: 10, fill: '#94a3b8' }} />
                        <Bar dataKey="completionRate" radius={[0, 6, 6, 0]} maxBarSize={22}>
                            {sorted.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry.completionRate)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </DashboardCard>
    );
};

export default TeamComplianceChartWidget;
